import { calculateContributionMarginFromUnitPrice } from '@calculations/price/utilities/contribution-margin';
import { decimalToPercent } from '@calculations/utilities/decimal-places';
import { DebouncedField } from '@ui/components/DebouncedField';
import { Field } from '@ui/components/Field';
import { PercentIcon } from '@ui/components/Icons';
import { actions } from '@ui/features/quote-builder/state/reducer';
import { selectQuoteBuilder } from '@ui/features/quote-builder/state/selectors/quote-builder';
import { selectQuotePrice } from '@ui/features/quote-builder/state/selectors/quote-price';
import { selectQuotePriceField } from '@ui/features/quote-builder/state/selectors/quote-price-field';
import { useDispatch } from '@ui/hooks/useDispatch';
import { useSelector } from '@ui/hooks/useSelector';

import { useQuotePriceContext } from './QuotePriceProvider';

export function ContributionMarginPercent() {
  const { quoteLineUuid, quotePriceUuid, isFirstQuotePrice } =
    useQuotePriceContext();

  const { elementId, value, issues } = useSelector((state) =>
    selectQuotePriceField(
      state,
      quoteLineUuid,
      quotePriceUuid,
      'contributionMargin',
    ),
  );

  const dispatch = useDispatch();

  function handleChange(margin: string) {
    dispatch(
      actions.contributionMarginChanged({
        quoteLineUuid,
        quotePriceUuid,
        value: margin,
      }),
    );
  }

  return (
    <DebouncedField
      id={elementId}
      label="Contribution Margin"
      showLabel={isFirstQuotePrice}
      leftIcon={PercentIcon}
      value={value}
      onChange={handleChange}
      inputMode="decimal"
      isInvalid={issues.length > 0}
      error={issues[0]?.description}
      debounceMs={1000}
    />
  );
}

export function ContributionMarginPercentReadOnly() {
  const config = useSelector(
    (state) => selectQuoteBuilder(state).calculationConfig,
  );

  const { quoteLineUuid, quotePriceUuid } = useQuotePriceContext();

  const { unitVariableCosts, unitPrice } = useSelector((state) =>
    selectQuotePrice(state, quoteLineUuid, quotePriceUuid),
  );

  let contributionMargin = null;

  if (unitVariableCosts && unitPrice && !unitPrice.eq(0)) {
    contributionMargin = calculateContributionMarginFromUnitPrice(
      {
        unitVariableCosts,
        unitPrice,
      },
      config,
    );

    contributionMargin = decimalToPercent(
      contributionMargin,
      config.decimalPlaces,
    );
  }

  return (
    <Field
      label="Contribution Margin"
      leftIcon={PercentIcon}
      isDisabled
      value={contributionMargin?.toString()}
    />
  );
}
